<template>
  <v-main>
    <v-container fluid>
      <v-row justify="center" class="mt-4 ml-4">
        <v-col>
          <span class="text-h5 font-weight-bold" v-text="'Daily Attendance'"></span>
          <v-btn class="mx-2 mb-2" fab x-small color="primary" to="/user-attendances/create">
            <v-icon dark> mdi-plus </v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <v-card elevation="6" class="py-4">
            <v-card-text class="pb-0">
              <v-row dense align="center">
                <v-col md="3" sm="6">
                  <v-text-field v-model="search_keyword" label="Search" prepend-inner-icon="mdi-magnify" outlined dense
                    hide-details clearable @keydown.enter="getData" @change="page = 1"></v-text-field>
                </v-col>
                <v-col md="3" sm="6">
                  <v-autocomplete ref="employee" v-model="user_id" :items="employees" label="Employee" @input="getData"
                    prepend-inner-icon="mdi-account" outlined dense hide-details clearable></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-autocomplete ref="supervisor" v-model="user_id" :items="supervisors" label="Supervisor"
                    @input="getData" prepend-inner-icon="mdi-account-tie" outlined dense hide-details
                    :disabled="user.roles && user.roles[0] && user.roles[0].name === 'SUPERVISOR'"
                    :clearable="!(user.roles && user.roles[0] && user.roles[0].name === 'SUPERVISOR')"></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-autocomplete ref="role" v-model="session_type_id" :items="sessionTypes" label="Session Type"
                    @input="getData" prepend-inner-icon="mdi-clipboard-check" outlined dense hide-details
                    clearable></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-autocomplete ref="status" v-model="active" :items="statuses" label="Employee Status"
                    @input="getData" prepend-inner-icon="mdi-toggle-switch" outlined dense
                    hide-details></v-autocomplete>
                </v-col>
              </v-row>
              <v-row dense align="center" class="mt-2 mb-1">
                <v-col md="2" sm="6" v-if="companies.length > 0">
                  <v-autocomplete v-model="filter_company_id" :items="companies" label="Company"
                    @input="getData" prepend-inner-icon="mdi-domain" outlined dense hide-details clearable></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-autocomplete ref="plant" v-model="plant_id" :items="plants" label="Plant" @input="getData"
                    prepend-inner-icon="mdi-factory" outlined dense hide-details clearable></v-autocomplete>
                </v-col>
                <v-col md="2" sm="6">
                  <v-menu v-model="start_date_menu" :close-on-content-click="false" :nudge-right="40"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="filter.start_date" label="Start Date" prepend-inner-icon="mdi-calendar"
                        readonly v-bind="attrs" v-on="on" outlined dense hide-details clearable
                        @click:clear="filter.start_date = null; getData()"></v-text-field>
                    </template>
                    <v-date-picker v-model="filter.start_date"
                      @change="(start_date_menu = false), getData()"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col md="2" sm="6">
                  <v-menu v-model="end_date_menu" :close-on-content-click="false" :nudge-right="40"
                    transition="scale-transition" offset-y min-width="auto">
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field v-model="filter.end_date" label="End Date" prepend-inner-icon="mdi-calendar"
                        readonly v-bind="attrs" v-on="on" outlined dense hide-details clearable
                        @click:clear="filter.end_date = null; getData()"></v-text-field>
                    </template>
                    <v-date-picker v-model="filter.end_date"
                      @change="(end_date_menu = false), getData()"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col md="2" sm="6">
                  <v-select v-model="selectedRowsPerPage" :items="rowsPerPageOptions" label="Records per page" outlined
                    dense hide-details @change="onRowsPerPageChange"></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col md="12">
          <v-card elevation="6">
            <v-card-title v-if="userAttendances.length != 0 || isLoading">
              <v-col md="4" sm="12" class="px-0">
                <v-pagination class="custom-pagination" v-model="page" :length="paginationLength" :total-visible="7"></v-pagination>
              </v-col>
              <v-spacer></v-spacer>
              <v-btn class="primary mx-4" v-if="!isExcelDownloaded" :loading="isExcelLoading"
                @click="generateExcel()">Generate Excel</v-btn>
              <v-btn v-else class="success mx-4" disabled>
                <v-icon left>mdi-check-circle</v-icon>Downloaded
              </v-btn>
              <download-excel ref="excelDownload" :title="title" :data="ExportUserAttendances" :fields="excel_fields"
                worksheet="UserAttendances" name="UserAttendances.xls" style="display: none">
              </download-excel>
            </v-card-title>
            <v-progress-linear v-if="isLoading" indeterminate color="primary"></v-progress-linear>
            <v-simple-table v-else fixed-header>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Sr No</th>
                    <th class="text-left">Employee Code</th>
                    <th class="text-left">Employee Name</th>
                    <th class="text-left">Date</th>
                    <th class="text-left">Login Time</th>
                    <th class="text-left">Logout Time</th>
                    <th class="text-left">Break Duration</th>
                    <th class="text-left">Session Type</th>
                    <th class="text-left">Supervisor</th>
                    <th class="text-right" v-if="roleName == 'SUPER ADMIN'">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody v-if="userAttendances.length != 0">
                  <tr v-for="(user, i) in userAttendances" :key="`user${i}`">
                    <td style="min-width: 85px">
                      <v-tooltip bottom v-if="user.active == 1">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" small dark color="green">
                            mdi-circle
                          </v-icon>
                        </template>
                        <span>User is Active</span>
                      </v-tooltip>
                      <v-tooltip bottom v-if="user.active == 0">
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" small dark color="red">
                            mdi-circle
                          </v-icon>
                        </template>
                        <span>User is InActive</span>
                      </v-tooltip>
                      &nbsp;
                      {{ i + 1 }}
                    </td>
                    <td>{{ user.user.employee_code || "-" }}</td>
                    <td>{{ user.user.name || "" }}</td>
                    <td>{{ user.date | dateFormat }}</td>
                    <td>
                      <span v-if="isWeeklyOff(user)">-</span>
                      <span v-else>{{ user.login_time | hhmm }}</span>
                    </td>
                    <td>
                      <span v-if="isWeeklyOff(user)">-</span>
                      <span v-else-if="user.logout_time">
                        {{ user.logout_time | hhmm }}
                        <v-chip v-if="isNextDay(user.login_date || user.date, user.logout_date || user.login_date || user.date)" x-small color="orange" text-color="white" class="ml-1">
                          +1d
                        </v-chip>
                      </span>
                      <v-chip v-else-if="isToday(user.date)" x-small color="green" text-color="white">
                        Currently Working
                      </v-chip>
                      <v-chip v-else x-small color="orange" text-color="white">
                        Missing Logout
                      </v-chip>
                    </td>
                    <td>
                      <span v-if="isWeeklyOff(user)">-</span>
                      <span v-else-if="getBreakDuration(user)">
                        {{ getBreakDuration(user).minutes }} min
                        <v-chip v-if="getBreakDuration(user).isExcess" x-small color="red" text-color="white" class="ml-1">
                          +{{ getBreakDuration(user).excessMinutes }} min
                        </v-chip>
                      </span>
                      <span v-else>-</span>
                    </td>
                    <td>
                      <span v-if="user.attendance_type === 'Leave' && user.leave_type">
                        {{ user.leave_type.code || user.leave_type.description || 'Leave' }}
                      </span>
                      <span v-else>{{ user.session_type ? user.session_type.code : "" }}</span>
                    </td>
                    <td>{{ user.user.supervisor ? user.user.supervisor.name : '' }}</td>

                    <td class="text-right" v-if="roleName == 'SUPER ADMIN'">
                      <v-btn class="mx-2" fab x-small color="primary" :to="`/user-attendances/${user.id}`">
                        <v-icon dark> mdi-pencil </v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr>
                    <td colspan="9" class="text-center">No data found</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";
import moment from "moment";

export default {
  name: "userAttendancesPage",
  data() {
    return {
      filter: {
        start_date: "",
        end_date: "",
      },
      page: 1,
      selectedRowsPerPage: this.$store.getters['settings/getRowsPerPage'] || 20,
      serialNoStarting: 1,
      paginationLength: 0,
      count: 0,
      search_keyword: "",
      supervisor_id: "",
      user_id: "",
      session_type_id: "",
      plant_id: "",
      filter_company_id: "",
      companies: [],
      active: true,
      start_date_menu: false,
      end_date_menu: false,
      attendance_type: "Normal",
      employees: [
        {
          id: 0,
          text: "Select Employee",
          value: "",
        },
      ],
      statuses: [
        {
          id: 0,
          text: "Select Employee Status",
          value: "",
        },
        { id: "ACTIVE", text: "ACTIVE", value: true },
        { id: "INACTIVE", text: "INACTIVE", value: false },
      ],

      supervisors: [
        {
          id: 0,
          text: "Select Supervisors",
          value: "",
        },
      ],
      sessionTypes: [
        {
          id: 0,
          text: "Select Session Type",
          value: "",
        },
      ],
      plants: [
        {
          id: 0,
          text: "Select Plant",
          value: "",
        },
      ],
      isLoading: false,
      isExcelLoading: false,
      isExcelDownloaded: false,
      userAttendances: [],
      ExportUserAttendances: [],
      title: "Daily Attendance Report",

      excel_fields: {
        "Sr No": "sr_no",
        "Employee Code": "employee_code",
        "Employee Name": "employee_name",
        Date: "date",
        "Login Time": "login_time",
        "Logout Time": "logout_time",
        "Break Duration (min)": "break_duration",
        "Allowed Break (min)": "allowed_break",
        "Excess Break (min)": "excess_break",
        "Session Type": "session_type",
        "Supervisor Name": "supervisor_name",
      },
    };
  },
  watch: {
    page: "getData",
  },
  created() {
    const created_at = this.$route.query.created_at;
    console.log("created_at:", created_at);
    this.filter.created_at = created_at;
    const session_type_id = this.$route.query.session_type_id;
    console.log("session_type_id:", session_type_id);
    this.session_type_id = session_type_id;

    this.getData();
  },
  mounted() {
    if (this.user.roles[0].name == "SUPERVISOR") {
      this.supervisor_id = this.user.id;
    }
    this.getData();
    this.getMasters();

    console.log(moment("11:29:30", ["HH:mm:ss"]).format("hh:mm A"));
  },
  methods: {
    onRowsPerPageChange(value) {
      this.$store.dispatch('settings/setRowsPerPage', value);
      this.page = 1;
      this.getData();
    },
    async getMasters() {
      let masters = await axios.get(`user_attendances/masters`);
      masters = masters.data;
      // Employees
      masters.employees.forEach((employee) => {
        if (this.user.roles[0].name == "SUPERVISOR") {
          if (employee.supervisor_id == this.user.id) {
            this.employees.push({
              id: employee.id,
              text: employee.name,
              value: employee.id,
            });
          }
        } else {
          this.employees.push({
            id: employee.id,
            text: employee.name,
            value: employee.id,
          });
        }
      });
      // Supervisors
      masters.supervisors.forEach((supervisor) => {
        if (this.user.roles[0].name == "SUPERVISOR") {
          if (supervisor.id == this.user.id) {
            this.supervisors.push({
              id: supervisor.id,
              text: supervisor.name,
              value: supervisor.id,
            });
          }
        } else {
          this.supervisors.push({
            id: supervisor.id,
            text: supervisor.name,
            value: supervisor.id,
          });
        }
      });
      // Session Types
      masters.sessionTypes.forEach((sessionType) => {
        this.sessionTypes.push({
          id: sessionType.id,
          text: sessionType.code,
          value: sessionType.id,
        });
      });
      // Plants
      if (masters.plants) {
        masters.plants.forEach((plant) => {
          this.plants.push({
            id: plant.id,
            text: plant.name || plant.description,
            value: plant.id,
          });
        });
      }
      // Companies (only for main company)
      if (masters.companies && masters.companies.length > 0) {
        this.companies = [
          { text: "All Companies", value: "all" },
        ];
        masters.companies.forEach((company) => {
          this.companies.push({
            text: company.name,
            value: company.id,
          });
        });
      }
    },
    async getData() {
      this.isLoading = true;
      this.search_keyword = this.search_keyword ? this.search_keyword : "";
      this.active = this.active ? true : false;
      this.filter.created_at = this.filter.created_at ? this.filter.created_at : "";
      this.user_id = this.user_id ? this.user_id : "";
      this.attendance_type = this.attendance_type ? this.attendance_type : "";
      this.session_type_id = this.session_type_id ? this.session_type_id : "";
      let userAttendances = [];
      let query =
        "page=" +
        this.page +
        "&rowsPerPage=" +
        this.selectedRowsPerPage +
        "&search_keyword=" +
        this.search_keyword +
        "&start_date=" +
        (this.filter.start_date || "") +
        "&end_date=" +
        (this.filter.end_date || "") +
        "&sessionTypeId=" +
        this.session_type_id +
        "&attendanceType=" +
        this.attendance_type +
        "&employee_status=" +
        this.active +
        "&userId=" +
        this.user_id +
        "&created_at=" +
        this.filter.created_at +
        "&plantId=" +
        (this.plant_id || "") +
        "&filter_company_id=" +
        (this.filter_company_id || "");
      userAttendances = await axios.get(`user_attendances?${query}`);
      this.userAttendances = userAttendances.data.data;
      this.ExportUserAttendances = [];
      this.isExcelDownloaded = false;
      console.log(this.userAttendances);
      this.count = userAttendances.data.count;
      this.serialNoStarting = (this.page - 1) * this.selectedRowsPerPage;
      this.paginationLength = Math.ceil(this.count / this.selectedRowsPerPage);
      this.isLoading = false;
    },
    //Generate Excel
    async generateExcel() {
      this.isExcelLoading = true;
      this.ExportUserAttendances = [];

      try {
        // Fetch all attendance data without pagination
        let query =
          "search_keyword=" +
          (this.search_keyword || "") +
          "&start_date=" +
          (this.filter.start_date || "") +
          "&end_date=" +
          (this.filter.end_date || "") +
          "&sessionTypeId=" +
          (this.session_type_id || "") +
          "&attendanceType=" +
          (this.attendance_type || "") +
          "&employee_status=" +
          this.active +
          "&userId=" +
          (this.user_id || "") +
          "&supervisorId=" +
          (this.supervisor_id || "") +
          "&plantId=" +
          (this.plant_id || "") +
          "&filter_company_id=" +
          (this.filter_company_id || "");

        console.log('Fetching attendance data with query:', query);
        let response = await axios.get(`user_attendances?${query}`);
        console.log('Response received:', response);

        if (!response || !response.data) {
          throw new Error('No response data received from server');
        }

        let allAttendances = response.data.data || [];

        // Transform data for Excel
        let sr_no = 1;
        allAttendances.forEach((attendance) => {
          let breakInfo = this.getBreakDuration(attendance);
          this.ExportUserAttendances.push({
            sr_no: sr_no++,
            employee_code: attendance.user?.employee_code || "-",
            employee_name: attendance.user?.name || "",
            date: this.$options.filters.dateFormat(attendance.date),
            login_time: this.$options.filters.hhmm(attendance.login_time),
            logout_time: this.$options.filters.hhmm(attendance.logout_time),
            break_duration: breakInfo ? breakInfo.minutes : "-",
            allowed_break: breakInfo ? breakInfo.allowedMinutes : "-",
            excess_break: breakInfo ? breakInfo.excessMinutes : "-",
            session_type: attendance.attendance_type === 'Leave' && attendance.leave_type
              ? (attendance.leave_type.code || attendance.leave_type.description || 'Leave')
              : (attendance.session_type?.code || ""),
            supervisor_name: attendance.user?.supervisor?.name || ""
          });
        });

        this.title = "Daily Attendance Report";
        this.isExcelLoading = false;
        this.isExcelDownloaded = true;
        // Auto-trigger download
        this.$nextTick(() => {
          if (this.$refs.excelDownload && this.$refs.excelDownload.$el) {
            this.$refs.excelDownload.$el.click();
          }
        });
      } catch (error) {
        console.error("Failed to generate Excel:", error);
        console.error("Error response:", error.response);
        const errorMsg = error.response?.data?.message || error.message || 'Unknown error';
        alert("Failed to generate Excel: " + errorMsg);
        this.isExcelLoading = false;
      }
    },

    getBreakDuration(attendance) {
      if (!attendance.break_start_time || !attendance.break_end_time) return null;
      let start = moment(attendance.break_start_time);
      let end = moment(attendance.break_end_time);
      let minutes = Math.round(end.diff(start, 'minutes', true));
      if (minutes <= 0) return null;

      // Calculate allowed break from shift
      let allowedMinutes = 0;
      if (attendance.shift && attendance.shift.break_start_time && attendance.shift.break_end_time) {
        let shiftBreakStart = moment(attendance.shift.break_start_time, 'HH:mm:ss');
        let shiftBreakEnd = moment(attendance.shift.break_end_time, 'HH:mm:ss');
        allowedMinutes = Math.round(shiftBreakEnd.diff(shiftBreakStart, 'minutes', true));
      }

      let excessMinutes = allowedMinutes > 0 ? Math.max(0, minutes - allowedMinutes) : 0;
      return {
        minutes: minutes,
        allowedMinutes: allowedMinutes,
        excessMinutes: excessMinutes,
        isExcess: excessMinutes > 0,
      };
    },
    isToday(date) {
      if (!date) return false;
      const today = moment().format("YYYY-MM-DD");
      const attendanceDate = moment(date).format("YYYY-MM-DD");
      return today === attendanceDate;
    },
    isWeeklyOff(attendance) {
      if (!attendance) return false;
      const sessionType = attendance.session_type?.description || attendance.session_type?.code || '';
      return sessionType.toUpperCase() === 'WEEKLY OFF' || attendance.is_weekly_off === true;
    },
    isNextDay(loginDate, logoutDate) {
      if (!loginDate || !logoutDate) return false;
      return loginDate !== logoutDate;
    },
  },
};
</script>
<style>
.custom-pagination .v-pagination{
  justify-content: start !important
}
</style>
